import React, { useEffect, useState } from "react";
import { useRecoilState } from "recoil";
import { tableDataState } from "../recoil/atoms/dataAtom";
import { getTable } from "../services/tableService.ts";
import { motion } from "framer-motion";
import { Skeleton, ImageWithSkeleton } from "./Skeleton";
import { useTableId } from "../utils/tableId";

const Nav = () => {
  const [tableData, setTableData] = useRecoilState(tableDataState);
  const [scrolled, setScrolled] = useState(false);
  const tableId = useTableId();

  /* Track scroll position for header glass upgrade */
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!tableId || tableData) return;
    const loadTable = async (id) => {
      try {
        const response = await getTable(id);
        if (response?.status === true) setTableData(response);
      } catch (error) {
        console.log("Failed to load table:", error);
      }
    };
    loadTable(tableId);
  }, [tableId, tableData, setTableData]);

  return (
    <motion.section
      initial={false}
      animate={{ y: 0, opacity: 1 }}
      className={`fixed top-0 w-full z-50 transition-all duration-400 header-border-glow ${
        scrolled
          ? "glass-panel-scrolled border-b border-[#22c55e]/18 shadow-[0_4px_28px_rgba(0,0,0,0.07)]"
          : "glass-panel border-b border-[#22c55e]/12"
      }`}
    >
      {/* Animated bottom glow line */}
      <div
        className="absolute bottom-0 left-[8%] right-[8%] h-[1px] pointer-events-none"
        style={{
          background: "linear-gradient(90deg, transparent, rgba(34,197,94,0.5), transparent)",
          opacity: scrolled ? 0.7 : 0.3,
          transition: "opacity 0.4s ease",
        }}
      />

      <div className="w-11/12 sm:w-19/20 md:w-9/10 lg:w-5/6 mx-auto flex items-center justify-between py-1 sm:py-2">

        {/* Logo */}
        <motion.div
          className="flex items-center gap-2 mt-3 -ml-10 sm:-ml-14 md:-ml-16"
          animate={{ scale: scrolled ? 0.96 : 1 }}
          transition={{ duration: 0.35, ease: "easeOut" }}
        >
          <ImageWithSkeleton
            src="/images/marstechlogo.webp"
            alt="Marstech Logo"
            className="w-48 h-16 sm:w-44 sm:h-14 md:w-52 md:h-16 object-contain float-logo"
            skeletonClassName="rounded-md"
          />
        </motion.div>

        {/* Table badge */}
        <motion.div
          className="flex items-center gap-1 sm:gap-3"
          whileHover={{ scale: 1.03 }}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
        >
          <div className="glass-pill px-4 py-1.5 flex items-center gap-2 border border-[#22c55e]/22 shadow-sm" style={{ borderRadius: "5px" }}>
            <div className="bg-green-50 p-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    className="w-6 h-6 text-[#009447] flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    {/* Table top */}
                    <path d="M6 8h12" />

                    {/* Left chair */}
                    <path d="M4 4v7c0 1.1.9 2 2 2h3" />
                    <path d="M6 13v6" />
                    <path d="M9 13v6" />

                    {/* Right chair */}
                    <path d="M20 4v7c0 1.1-.9 2-2 2h-3" />
                    <path d="M18 13v6" />
                    <path d="M15 13v6" />

                    {/* Chair seats */}
                    <path d="M6 11h3" />
                    <path d="M15 11h3" />
                  </svg>
                </div>
            <h1 className="text-gray-800 font-bold text-xs sm:text-sm md:text-base whitespace-nowrap flex items-center gap-1">
              {tableId?.startsWith("B") ? "Branch:" : "Table No:"}
              {tableData ? (
                <span className="text-green-600 ml-1">{tableData.name}</span>
              ) : tableId ? (
                <span className="text-green-600 ml-1">{tableId}</span>
              ) : (
                <Skeleton className="w-12 h-5 rounded-md ml-1" />
              )}
            </h1>
          </div>
        </motion.div>

      </div>
    </motion.section>
  );
};

export default Nav;
